import React, { useState, useEffect } from 'react'
import { Box, Paper, Typography, Skeleton, useTheme, alpha, Grid } from '@mui/material'
import { Users, UserCheck, Activity, Eye, Clock, UserPlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { analyticsApi } from '../../../../api/analyticsApi'
import KPICard from '../cards/KPICard'
import ActiveUsersChart from '../charts/ActiveUsersChart'

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '0s'
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  if (mins === 0) return `${secs}s`
  return `${mins}m ${secs}s`
}

const OverviewTab = ({ period }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const goldColor = theme.palette.primary.main

  const [overview, setOverview] = useState(null)
  const [activeUsers, setActiveUsers] = useState(null)
  const [topPages, setTopPages] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const [overviewRes, activeUsersRes, topPagesRes] = await Promise.all([
          analyticsApi.getOverview(period),
          analyticsApi.getActiveUsers(period),
          analyticsApi.getTopPages(period),
        ])
        setOverview(overviewRes.data)
        setActiveUsers(activeUsersRes.data)
        setTopPages(topPagesRes.data)
      } catch (err) {
        console.error('Failed to fetch overview data:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [period])

  const kpiCards = [
    { label: t('analytics_dau'), icon: Users, metric: overview?.dau },
    { label: t('analytics_mau'), icon: UserCheck, metric: overview?.mau },
    { label: t('analytics_total_sessions'), icon: Activity, metric: overview?.total_sessions },
    { label: t('analytics_total_pageviews'), icon: Eye, metric: overview?.total_pageviews },
    { label: t('analytics_avg_duration'), icon: Clock, metric: overview?.avg_duration, format: formatDuration },
    { label: t('analytics_new_users'), icon: UserPlus, metric: overview?.new_users },
  ]

  const pages = topPages?.pages || topPages || []

  return (
    <Box
      sx={{
        overflow: 'auto',
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}
    >
      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpiCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.label}>
            <KPICard
              label={card.label}
              icon={card.icon}
              metric={card.metric}
              accentColor={goldColor}
              loading={loading}
              format={card.format}
            />
          </Grid>
        ))}
      </Grid>

      {/* Active Users Chart */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          mb: 3,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          {t('analytics_active_users_trend')}
        </Typography>
        <ActiveUsersChart
          data={activeUsers?.trend || activeUsers || []}
          loading={loading}
          height={300}
        />
      </Paper>

      {/* Top Pages Table */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          {t('analytics_top_pages')}
        </Typography>

        {loading ? (
          <>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} variant="text" width="100%" height={36} sx={{ mb: 0.5 }} />
            ))}
          </>
        ) : pages.length === 0 ? (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {t('analytics_no_page_data')}
          </Typography>
        ) : (
          <>
            {/* Table Header */}
            <Box
              sx={{
                display: 'flex',
                py: 1,
                px: 1.5,
                borderRadius: '8px',
                background: alpha(theme.palette.text.primary, 0.03),
                mb: 0.5,
              }}
            >
              <Box sx={{ flex: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.05em' }}>
                  {t('analytics_page_url')}
                </Typography>
              </Box>
              <Box sx={{ width: 100, textAlign: 'right' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.05em' }}>
                  {t('analytics_views')}
                </Typography>
              </Box>
              <Box sx={{ width: 120, textAlign: 'right' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary, textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: '0.05em' }}>
                  {t('analytics_unique_visitors')}
                </Typography>
              </Box>
            </Box>

            {/* Table Rows */}
            {pages.map((page, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  py: 1.2,
                  px: 1.5,
                  borderRadius: '6px',
                  transition: 'background 0.15s',
                  '&:hover': { background: alpha(theme.palette.text.primary, 0.02) },
                }}
              >
                <Box sx={{ flex: 2, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.82rem', color: theme.palette.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {page.page_url || page.url || page.path}
                  </Typography>
                </Box>
                <Box sx={{ width: 100, textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: theme.palette.text.primary }}>
                    {(page.views ?? page.count ?? 0).toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ width: 120, textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.82rem', color: theme.palette.text.secondary }}>
                    {(page.unique_visitors ?? page.unique ?? 0).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            ))}
          </>
        )}
      </Paper>
    </Box>
  )
}

export default OverviewTab
